import {
  Button,
  Flex,
  Menu,
  MenuRoot,
  MenuRow,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import {
  ObservationsV1IssueState as IssueState,
  ObservationsV1IssueAutonomyLevel,
  ObservationsV1IssueCloseReason,
  ObservationsV1IssueStatus,
} from '@spotify/confidence-graphql';
import { useEnsureFavorite } from '@spotify/plugin-confidence-collections-react';
import {
  ISSUE_CLOSE_REASONS,
  IssueFragment,
  getAutonomyLevelLabel,
  getIssueCloseReasonId,
  getIssueCloseReasonLabel,
  useReopenIssue,
  useReviewIssue,
  useStartIssue,
  useWaitIssue,
} from '@spotify/plugin-confidence-issues-react';
import {
  AutoTrackingScope,
  TrackingScope,
} from '@spotify/plugin-confidence-tracking-react';

import { useIssueClosure } from '../../hooks';
import { isIssueSnoozed } from '../../utils/isIssueSnoozed';

const APPROVE_AUTONOMY_LEVEL_OPTIONS: ObservationsV1IssueAutonomyLevel[] = [
  ObservationsV1IssueAutonomyLevel.Build,
  ObservationsV1IssueAutonomyLevel.Full,
];

type IssueStateActionsProps = {
  canEdit: boolean;
  issue: IssueFragment;
};

type IssueActionProps = {
  disabled: boolean;
  issueName: string;
};

const StartAction = ({ disabled, issueName }: IssueActionProps) => {
  const { startIssue, isMutating } = useStartIssue({ onError: 'post-alert' });

  return (
    <Button
      type="button"
      variant="primary"
      size="small"
      disabled={disabled || isMutating}
      isLoading={isMutating}
      loadingAriaLabel="Starting issue"
      data-track-id="button.start-issue"
      data-track-action="update"
      onClick={() => {
        void startIssue(issueName);
      }}
     data-track-source-anchor="2ef43b8b76;;high">
      Start
    </Button>
  );
};

const WaitAction = ({ disabled, issueName }: IssueActionProps) => {
  const { waitIssue, isMutating } = useWaitIssue({ onError: 'post-alert' });

  return (
    <Button
      type="button"
      variant="secondary-bordered"
      size="small"
      disabled={disabled || isMutating}
      isLoading={isMutating}
      loadingAriaLabel="Moving issue to waiting"
      data-track-id="button.wait-issue"
      data-track-action="update"
      onClick={() => {
        void waitIssue(issueName);
      }}
     data-track-source-anchor="58095e467a">
      Wait
    </Button>
  );
};

const ReopenAction = ({ disabled, issueName }: IssueActionProps) => {
  const { reopenIssue, isMutating } = useReopenIssue({
    onError: 'post-alert',
  });

  return (
    <Button
      type="button"
      variant="secondary-bordered"
      size="small"
      disabled={disabled || isMutating}
      isLoading={isMutating}
      loadingAriaLabel="Reopening issue"
      data-track-id="button.reopen-issue"
      data-track-action="update"
      onClick={() => {
        void reopenIssue(issueName);
      }}
     data-track-source-anchor="7b736502b8">
      Reopen
    </Button>
  );
};

const CloseAction = ({
  disabled,
  issue,
}: {
  disabled: boolean;
  issue: IssueFragment;
}) => {
  const issueName = issue.name;
  const { requestClose, isMutating, closeDialog } = useIssueClosure();

  return (
    <>
      <MenuRoot>
        <Button
          type="button"
          variant="secondary-bordered"
          size="small"
          disabled={disabled || isMutating}
          isLoading={isMutating}
          loadingAriaLabel="Closing issue"
          data-track-id="button.close-issue"
          data-track-action="open-menu"
         data-track-source-anchor="6df858cbb9">
          Close
        </Button>
        <Popover placement="bottomStart" hasNoArrow>
          <TrackingScope elementId="group.issue-state-actions">
            <AutoTrackingScope
              elementId="menu.close-issue"
              data={{ issue: issueName }}
            >
              <Menu aria-label="Close issue">
                {ISSUE_CLOSE_REASONS.map(closeReason => (
                  <MenuRow
                    key={closeReason}
                    id={closeReason}
                    textValue={getIssueCloseReasonLabel(closeReason)}
                    data-track-id={`menuitem.${getIssueCloseReasonId(closeReason)}`}
                    data-track-action={
                      closeReason === ObservationsV1IssueCloseReason.Fixed
                        ? 'update'
                        : 'open-dialog'
                    }
                    onAction={() => {
                      requestClose(issue, closeReason);
                    }}
                   data-track-source-anchor="8de3668aa9;close-action-menu-row;low">
                    {getIssueCloseReasonLabel(closeReason)}
                  </MenuRow>
                ))}
              </Menu>
            </AutoTrackingScope>
          </TrackingScope>
        </Popover>
      </MenuRoot>
      {closeDialog}
    </>
  );
};

const ApproveAction = ({ disabled, issueName }: IssueActionProps) => {
  const { reviewIssue, isMutating } = useReviewIssue({
    onError: 'post-alert',
  });
  const { ensureFavorite } = useEnsureFavorite();

  return (
    <MenuRoot>
      <Button
        type="button"
        variant="primary"
        size="small"
        disabled={disabled || isMutating}
        isLoading={isMutating}
        loadingAriaLabel="Approving issue"
        data-track-id="button.approve-issue"
        data-track-action="open-menu"
       data-track-source-anchor="a886f847bb;;high">
        Approve
      </Button>
      <Popover placement="bottomStart" hasNoArrow>
        <TrackingScope elementId="group.issue-state-actions">
          <AutoTrackingScope
            elementId="menu.approve-issue"
            data={{ issue: issueName }}
          >
            <Menu aria-label="Approve with autonomy level">
              {APPROVE_AUTONOMY_LEVEL_OPTIONS.map(autonomyLevel => (
                <MenuRow
                  key={autonomyLevel}
                  id={autonomyLevel}
                  textValue={getAutonomyLevelLabel(autonomyLevel)}
                  data-track-id={`menuitem.approve-${autonomyLevel.toLowerCase()}`}
                  data-track-action="update"
                  onAction={() => {
                    void reviewIssue(issueName, [], autonomyLevel).then(() =>
                      ensureFavorite(issueName),
                    );
                  }}
                 data-track-source-anchor="63482dadd2;approve-action-menu-row;low">
                  {getAutonomyLevelLabel(autonomyLevel)}
                </MenuRow>
              ))}
            </Menu>
          </AutoTrackingScope>
        </TrackingScope>
      </Popover>
    </MenuRoot>
  );
};

export const IssueStateActions = ({
  canEdit,
  issue,
}: IssueStateActionsProps) => {
  const actionProps: IssueActionProps = {
    disabled: !canEdit || isIssueSnoozed(issue.snoozedUntilTime),
    issueName: issue.name,
  };
  const isWorking = issue.state === IssueState.Working;
  const isWaitingForReview =
    issue.status === ObservationsV1IssueStatus.WaitingForReview;

  return (
    <Flex
      gap="tighter-2"
      data-track-scope="group.issue-state-actions"
      data-track-data-issue={issue.name}
    >
      {issue.state === IssueState.Closed ? (
        <ReopenAction {...actionProps} />
      ) : (
        <>
          {isWaitingForReview && <ApproveAction {...actionProps} />}
          {!isWaitingForReview && !isWorking && (
            <StartAction {...actionProps} />
          )}
          {isWorking && <WaitAction {...actionProps} />}
          <CloseAction disabled={actionProps.disabled} issue={issue} />
        </>
      )}
    </Flex>
  );
};
